import { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import SEO from '../components/SEO';
import { track } from '../lib/track';
import { trackEvent } from '../lib/analytics';

export default function LTDOwnersSuccessPage() {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(true);
  const sessionId = searchParams.get('session_id');
  const planName = searchParams.get('plan_name') || searchParams.get('plan');
  const trackedSessionIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Clear checkout/session flags
    sessionStorage.removeItem('ltd_checkout_intent');
    sessionStorage.removeItem('stripe_checkout_initiated');

    // Fire conversion events once per session
    const effectiveSessionId = sessionId || 'webhook-processed';
    if (trackedSessionIdRef.current !== effectiveSessionId) {
      trackedSessionIdRef.current = effectiveSessionId;
      track('purchase_completed', {
        persona: 'camp_owner',
        product_type: 'ltd_owner',
        stripe_session_id: sessionId || 'webhook-processed',
        order_id: sessionId || 'webhook-processed',
        currency: 'USD',
        plan_name: planName || undefined,
      });
      trackEvent('LTD-Owners-Conversion', {
        plan: planName || 'ltd-owner',
        session_id: sessionId || 'webhook-processed',
      });
    }

    // Invalidate profile so LTDOwnersPage shows hasOwnerLTD when they go back
    queryClient.invalidateQueries({ queryKey: ['profile'] });
    queryClient.invalidateQueries({ queryKey: ['owner-lifetime-access'] });

    const timer = setTimeout(() => setIsLoading(false), 1200);
    return () => clearTimeout(timer);
  }, [sessionId, planName, queryClient]);

  if (isLoading) {
    return (
      <div className="page-container" style={{ minHeight: 'auto' }}>
        <div className="page-content flex items-center justify-center" style={{ minHeight: '60vh' }}>
          <div className="max-w-2xl mx-auto text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              Setting up your lifetime access...
            </h1>
            <p className="text-gray-600">
              Please wait while we confirm your founding camp access.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" style={{ minHeight: 'auto' }}>
      <SEO
        title="Lifetime Access Activated | CampWhiz for Camp Owners"
        description="Your founding camp lifetime access has been activated successfully."
        url="/ltd/owners/success"
        noindex={true}
      />

      <div className="page-content py-12" style={{ minHeight: 'auto' }}>
        <div className="max-w-2xl mx-auto text-center">
          <div className="mb-8">
            <CheckCircleIcon className="h-24 w-24 text-green-500 mx-auto mb-6" />
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Lifetime Access Activated! 🎉
            </h1>
            <p className="text-lg text-gray-600 mb-6">
              Your founding camp lifetime access is now active. You can claim and manage your camp listing — for life.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-green-800 mb-3">
              What&apos;s Next?
            </h2>
            <ul className="text-left text-green-700 space-y-2">
              <li>✅ Lifetime profile ownership — pay once, benefit forever</li>
              <li>✅ Edit description, hours, and photos anytime</li>
              <li>✅ Control how parents see your camp</li>
              <li>
                ✅ If you haven&apos;t yet, claim your camp so it&apos;s under your control
              </li>
            </ul>
          </div>

          <div className="space-y-4">
            <Link
              to="/mycamp-teaser"
              className="inline-block bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              Go to My Camp
            </Link>
            <div>
              <Link
                to="/ltd/owners"
                className="text-primary hover:text-primary/80 font-medium"
              >
                Back to LTD for Camp Owners
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
